import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import apiService from '../../utils/apiService';
import { getThemeColor } from '../../config/appConfig';
import { getChallengePhase, normalizeChallengeOverview } from '../../pages/challenges/challengesData';

const THEME = getThemeColor('primary');
const BANNER_SRC = '/images/challenges/weekly-challenge-banner.jpg';

// Dashboard and Dashboard 2 are both "the Dashboard" for this popup.
const DASHBOARD_PATHS = ['/', '/dashboard-2'];

/**
 * Shows the Weekly Challenge banner every time the affiliate loads the Dashboard or Dashboard 2 (no
 * per-tab/session suppression — it fires again on every fresh mount, i.e. every login
 * and every full page reload of the Dashboard). Loading or refreshing any other screen
 * does not trigger it. Mounted once in PartnerPageLayout so it can react to whichever
 * route is current.
 */
const ChallengeLoginModal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!DASHBOARD_PATHS.includes(location.pathname)) return undefined;

    let cancelled = false;

    const load = async () => {
      try {
        const res = await apiService.getChallengeOverview();
        if (cancelled || !res.success || !res.data) return;

        const normalized = normalizeChallengeOverview(res.data);
        if (!normalized.isActive) return;

        const phase = getChallengePhase(normalized.startDate, normalized.endDate);
        if (phase === 'ended') return;

        setOverview(normalized);
        setOpen(true);
      } catch {
        /* silent — popup is a nice-to-have, never block the app on it */
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && handleClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleView = () => {
    handleClose();
    navigate('/challenges/list');
  };

  if (!open || !overview) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        onClick={handleClose}
        aria-label="Close"
      />
      <div
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="challenge-popup-title"
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-2.5 right-2.5 z-10 w-7 h-7 rounded-full bg-black/35 hover:bg-black/55 backdrop-blur-sm flex items-center justify-center transition"
        >
          <X className="w-3.5 h-3.5 text-white" />
        </button>
        <img src={BANNER_SRC} alt={overview.title} className="w-full h-auto" />
        <div className="p-5">
          <h2 id="challenge-popup-title" className="sr-only">
            {overview.title}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Bring more trading volume to earn extra commission this week. Trade{' '}
            <strong>{overview.minimumLots.toLocaleString('en-US')}</strong> standard lots or more and earn{' '}
            <strong>${overview.commissionPerLot}</strong> per lot.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2.5 rounded-xl text-sm font-medium border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              Maybe later
            </button>
            <button
              type="button"
              onClick={handleView}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition"
              style={{ backgroundColor: THEME }}
            >
              View Challenge
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChallengeLoginModal;
